/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from '@wec-core/form-engine';
import {
  fulFillmentActions,
  authActions,
  userActions,
  checkoutActions
} from '@detox/actions';

import { trans as t } from '../../../helpers/localisation';
import {
  BillingPrefType,
  ErrorScenarioType,
  FormConfig,
  TObject
} from '../../../types/registrationCheckout';

import { getBillingGroup, getFormDataMapping, getSubmitGroup } from '../helper';

import {
  MyInfoFormState,
  getCISMyInfoReadOnlyPersonalData,
  getFilteredExistingValues,
  getValidationSchema,
  getInitialValues,
  getUpdatedCISMyInfoValues
} from '../myinfoHelper';

import LoadingOverlayComponent from '../../LoadingOverlay';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useCheckOutHelper } from '../hooks/useCheckOutHelper';
import { useMcssVerifyEkyc } from '../../../hooks/useEkycEnhancements';
import useProcessFormFields from '../../../hooks/useProcessFormFields';

interface TProps {
  onValuesChange?: (values: TObject) => void;
  billingPrefData: BillingPrefType[];
  errorScenariosData?: ErrorScenarioType[];
  onErrorScenarioFound?: (values: TObject) => void;
  onFormSubmit?: (values: TObject, navigateTo: string) => void;
  goToOrderSummary: () => void;
  onGettingFormConfigs: (formConfigs: FormConfig) => void;
  handleCheckoutHistoryState?: (needPushState?: boolean) => void;
}

const submitGroup = getSubmitGroup(t as never, false, false, 'Proceed');

const formData = {};

const CISMyInfoFlow: React.FC<TProps> = ({
  onValuesChange,
  billingPrefData,
  errorScenariosData,
  onErrorScenarioFound,
  onFormSubmit,
  goToOrderSummary,
  onGettingFormConfigs,
  handleCheckoutHistoryState
}): ReactElement => {
  const dispatch = useDispatch();
  const { getCISMyInfo } = authActions;
  const {
    addressInfo,
    checkout,
    cisInformation,
    fulfillment,
    orderSummary,
    ekycState,
    userInformation
  } = useSelector((state: TObject) => ({
    order: state.order,
    addressInfo: state.fulfillment?.addressInfo,
    checkout: state.checkout,
    cisInformation: state.user?.cis?.information,
    fulfillment: state.fulfillment,
    orderSummary: state.orderSummary,
    ekycState: state.ekyc,
    userInformation: state.user?.information
  }));
  const { renderError } = useErrorHandler({
    states: [checkout]
  });
  const { handleOnFormSubmit } = useCheckOutHelper({
    onErrorScenarioFound,
    errorScenariosData,
    billingPrefData,
    onFormSubmit,
    goToOrderSummary,
    onGettingFormConfigs
  });

  const [myInfoFormState, setMyInfoFormState] = useState<MyInfoFormState>({
    formValues: {},
    formInputsMapping: []
  });

  const { isLoading: addressLoading, address } = addressInfo || {};
  const { checkoutFormData = {}, isLoading } = checkout;
  const fulfilmentLoading = fulfillment?.delivery?.isLoading || false;
  const orderSummaryLoading = orderSummary?.isShowSkeletonLoader || false;
  const userMyInfo = cisInformation?.cusMyInfo;
  const isCISFlow = Boolean(cisInformation?.rates);
  const isMyInfoLoading = Boolean(userMyInfo?.loading);
  const isCisUserLoginTypeMyInfo = Boolean(cisInformation?.$myInfo);
  const billingPrefGroup = getBillingGroup(t as never, billingPrefData);
  const verifyEkycMcssPayload = ekycState?.verifyEkycPayload || {};
  const postalCode = userMyInfo?.address?.postal;
  const { email, userData } = getCISMyInfoReadOnlyPersonalData(userMyInfo);
  const retrieveContactData = checkout?.checkoutRetrieveContactData;

  const setOverAllFormState = (): void => {
    const formFieldsData = {
      billingPref: billingPrefGroup,
      submitGroup: submitGroup
    };
    const filteredExistingValues = getFilteredExistingValues(checkoutFormData);
    const initialFormValues = {
      ...getInitialValues({ isCISFlow }),
      ...filteredExistingValues
    };

    setMyInfoFormState({
      formInputsMapping: formFieldsData,
      formValues: initialFormValues
    });
  };

  const getAddressInfoState = async (postalCode: string) => {
    await dispatch(fulFillmentActions.getAddressInfo(postalCode, true));
  };

  useMcssVerifyEkyc();

  const formattedFormFieldsData = getFormDataMapping(
    myInfoFormState?.formInputsMapping
  );

  const { processedFormFieldsData } = useProcessFormFields({
    formFieldsData: formattedFormFieldsData
  });

  useEffect(() => {
    if (!userMyInfo && !userMyInfo?.customerId) {
      dispatch(
        getCISMyInfo({
          sessionToken: cisInformation?.$myInfo?.accessToken,
          isCIS: true
        })
      );
    }
  }, [isCisUserLoginTypeMyInfo, isCISFlow]);

  useEffect(() => {
    if (handleCheckoutHistoryState) {
      handleCheckoutHistoryState();
    }
    if (postalCode) {
      getAddressInfoState(postalCode);
    }
  }, []);

  useEffect(() => {
    if (userMyInfo) {
      setOverAllFormState();
    }
  }, [userMyInfo]);

  useEffect(() => {
    if (verifyEkycMcssPayload?.contactExists) {
      dispatch(userActions.getUserInformation());
    }
  }, [verifyEkycMcssPayload?.contactExists]);

  useEffect(() => {
    if (
      verifyEkycMcssPayload?.contactExists &&
      userInformation?.anonymousContactId
    ) {
      dispatch(
        checkoutActions.retrieveContactDetails(
          userInformation?.anonymousContactId
        )
      );
    }
  }, [
    verifyEkycMcssPayload?.contactExists,
    userInformation?.anonymousContactId
  ]);

  // before rendering the form
  if (isMyInfoLoading) {
    return <LoadingOverlayComponent />;
  }

  return processedFormFieldsData && processedFormFieldsData.length > 0 ? (
    <>
      <Form
        enableReinitialize={false}
        formFieldsConfig={processedFormFieldsData}
        initialValues={myInfoFormState.formValues}
        data={formData}
        callbacks={{}}
        onSubmit={values => {
          onValuesChange(values);

          const updatedValues = getUpdatedCISMyInfoValues(
            values,
            email,
            userMyInfo,
            verifyEkycMcssPayload
          );
          handleOnFormSubmit(updatedValues, {
            passType: retrieveContactData?.documentType,
            id: retrieveContactData?.documentId
          });
        }}
        enableInputErrorFocus
      />

      {renderError()}

      {(isLoading || fulfilmentLoading || orderSummaryLoading) && (
        <LoadingOverlayComponent />
      )}
    </>
  ) : null;
};
export default CISMyInfoFlow;
